import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pingRefundApi, type PingRefund } from '../services/api';

export function PingRefundPanel({
  projectId,
  refreshToken,
  canSimulate,
}: {
  projectId: string;
  refreshToken: number;
  canSimulate: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [operations, setOperations] = useState<PingRefund[]>([]);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(false);
  const acting = useRef(false);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      try {
        const rows = await pingRefundApi.list(projectId);
        if (!active) return;
        setOperations(rows);
        setError(false);
        if (rows.some((r) => !['confirmed', 'returned'].includes(r.state)))
          timer = setTimeout(() => void load(), 10000);
      } catch {
        if (active) setError(true);
      }
    };
    void load();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [projectId, refreshToken, revision]);
  const act = async (op: PingRefund, account: boolean) => {
    if (acting.current) return;
    acting.current = true;
    setBusy(true);
    setError(false);
    try {
      if (account) {
        await pingRefundApi.start(op.paymentId);
        await pingRefundApi.simulateAccount(op.paymentId);
      } else await pingRefundApi.disburse(op.id);
    } catch {
      setError(true);
      setOperations((rows) =>
        rows.map((row) =>
          row.id === op.id
            ? {
                ...row,
                state: 'review_required',
                errorCode: 'ping_refund_outcome_uncertain',
              }
            : row,
        ),
      );
    } finally {
      setRevision((r) => r + 1);
      acting.current = false;
      setBusy(false);
    }
  };
  if (!operations.length)
    return error ? (
      <p className="alert alert-warning" role="status">
        {t('pingRefund.loadError')}
      </p>
    ) : null;
  return (
    <section className="card mb-4" aria-label={t('pingRefund.title')}>
      <div className="card-body">
        <h2 className="h5">{t('pingRefund.title')}</h2>
        <p className="alert alert-info">{t('pingRefund.simulation')}</p>
        {error && (
          <p className="alert alert-warning" role="status">
            {t('pingRefund.loadError')}
          </p>
        )}
        <div className="row g-3">
          {operations.map((op) => (
            <div className="col-12 col-lg-6" key={op.id}>
              <article className="border rounded p-3 h-100 text-break">
                <h3 className="h6">
                  {new Intl.NumberFormat(i18n.language, {
                    style: 'currency',
                    currency: 'SEK',
                  }).format(op.amountOre / 100)}
                </h3>
                <p role="status">{t(`pingRefund.states.${op.state}`)}</p>
                {op.errorCode && (
                  <p className="alert alert-warning">
                    {t('pingRefund.review')}
                  </p>
                )}
                <dl className="mb-3 small">
                  <dt>{t('pingRefund.contract')}</dt>
                  <dd>{op.sourceContractId}</dd>
                  <dt>{t('pingRefund.operation')}</dt>
                  <dd>{op.id}</dd>
                  <dt>{t('pingRefund.credit')}</dt>
                  <dd>{op.creditId}</dd>
                </dl>
                <details className="mb-3">
                  <summary className="py-3">{t('pingRefund.history')}</summary>
                  <ol className="mt-2">
                    {op.history.slice(-10).map((row, index) => (
                      <li key={index}>
                        {t(`pingRefund.states.${row.state}`)} ·{' '}
                        {new Intl.DateTimeFormat(i18n.language, {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        }).format(new Date(row.observedAt))}
                      </li>
                    ))}
                  </ol>
                </details>
                {canSimulate && op.state === 'waiting_ais' && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-lg w-100"
                    disabled={busy}
                    onClick={() => void act(op, true)}
                  >
                    {t('pingRefund.simulateAccount')}
                  </button>
                )}
                {canSimulate && op.state === 'awaiting_payout' && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-lg w-100"
                    disabled={busy}
                    onClick={() => void act(op, false)}
                  >
                    {t('pingRefund.simulatePayout')}
                  </button>
                )}
              </article>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
